function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { number, string, func, elementType, bool } from "prop-types";
import classNamesMaker from "classnames";
import { Button } from "#library/components/Button";
import { useMediaQueries, MEDIAQUERY_BREAKPOINTS } from "#library/hooks/useMediaQueries";
import IconArrowLeft from "#library/assets/icons/jds_arrowLeft.svg";
import IconArrowRight from "#library/assets/icons/jds_arrowRight.svg";
import { usePagination, DOTS } from "#library/components/Pagination/usePagination";
import { useCurrentPage } from "#library/components/Pagination/useCurrentPage";
import styles from "#library/components/Pagination/pagination.module.css";
var MINIMUM_PAGES = 2;
var DEFAULT_MOBILE_SIBLINGS = 0;
var DEFAULT_DESKTOP_SIBLINGS = 1;
export var Pagination = function Pagination(_ref) {
  var _ref$ariaLabelNextBut = _ref.ariaLabelNextButton,
    ariaLabelNextButton = _ref$ariaLabelNextBut === void 0 ? "Next" : _ref$ariaLabelNextBut,
    _ref$ariaLabelPrevBut = _ref.ariaLabelPrevButton,
    ariaLabelPrevButton = _ref$ariaLabelPrevBut === void 0 ? "Previous" : _ref$ariaLabelPrevBut,
    className = _ref.className,
    _ref$isAutonomous = _ref.isAutonomous,
    isAutonomous = _ref$isAutonomous === void 0 ? false : _ref$isAutonomous,
    _ref$itemPerPage = _ref.itemPerPage,
    itemPerPage = _ref$itemPerPage === void 0 ? 12 : _ref$itemPerPage,
    _ref$linkComponent = _ref.linkComponent,
    linkComponent = _ref$linkComponent === void 0 ? "a" : _ref$linkComponent,
    linkParam = _ref.linkParam,
    _ref$linkPathname = _ref.linkPathname,
    linkPathname = _ref$linkPathname === void 0 ? "" : _ref$linkPathname,
    linkSearchParams = _ref.linkSearchParams,
    onPaginate = _ref.onPaginate,
    totalCount = _ref.totalCount,
    _ref$withOnlyArrows = _ref.withOnlyArrows,
    withOnlyArrows = _ref$withOnlyArrows === void 0 ? false : _ref$withOnlyArrows,
    _ref$withScrollToTop = _ref.withScrollToTop,
    withScrollToTop = _ref$withScrollToTop === void 0 ? false : _ref$withScrollToTop;
  var isLinkAnchor = linkComponent === "a";
  var isMediumBreakPoint = useMediaQueries(MEDIAQUERY_BREAKPOINTS.MEDIUM);
  var currentSiblingCount = isMediumBreakPoint ? DEFAULT_MOBILE_SIBLINGS : DEFAULT_DESKTOP_SIBLINGS;
  var _useCurrentPage = useCurrentPage({
      itemPerPage: itemPerPage,
      initSearchParams: linkSearchParams
    }),
    currentPage = _useCurrentPage.currentPage,
    setCurrentPage = _useCurrentPage.setCurrentPage;
  var _usePagination = usePagination({
      currentPage: currentPage,
      totalCount: totalCount,
      siblingCount: currentSiblingCount,
      itemPerPage: itemPerPage
    }),
    paginationRange = _usePagination.paginationRange,
    isFirstPage = _usePagination.isFirstPage,
    isLastPage = _usePagination.isLastPage,
    totalPageCount = _usePagination.totalPageCount;
  var onPageChange = function onPageChange(event, pageNumber) {
    if (isAutonomous) {
      event.preventDefault();
    }

    /*
     * startIndex & endIndex
     * are used to navigate into a data array with the slice method
     * This can be useful if you are paging on an array of static data without a paginate API.
     *
     * e.g :
     * pageNumber = 4
     * itemPerPage = 10
     *
     * startIndex = (pageNumber (4) - 1) * itemPerPage (10) // equal to 30
     * endIndex  = startIndex (30) + itemPerPage (10) // equal to 40
     *
     * We can use a slice method to return a portion of an array
     * into a new array object selected from startIndex to endIndex
     * where startIndex and endIndex represent the index of items in that array.
     */
    var startIndex = (pageNumber - 1) * itemPerPage;
    var endIndex = startIndex + itemPerPage;
    setCurrentPage(pageNumber);
    if (typeof window !== "undefined" && withScrollToTop && !isLinkAnchor) {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
    if (onPaginate) {
      onPaginate({
        pageNumber: pageNumber,
        startIndex: startIndex,
        endIndex: endIndex
      });
    }
  };
  var onNext = function onNext(event) {
    onPageChange(event, currentPage + 1);
  };
  var onPrevious = function onPrevious(event) {
    onPageChange(event, currentPage - 1);
  };
  var generateLink = function generateLink(pageNumber) {
    var searchParams = new URLSearchParams(linkSearchParams);
    searchParams.set("page", pageNumber);
    return "".concat(linkPathname, "?").concat(searchParams.toString());
  };
  var noValidCurrentPage = Number.isNaN(Number.parseFloat(currentPage)) || currentPage < 1;
  var currentPageBeyondTotalPages = currentPage > totalPageCount;
  var lessThanMinimumPages = paginationRange.length < MINIMUM_PAGES;
  if (noValidCurrentPage || currentPageBeyondTotalPages || lessThanMinimumPages) {
    return null;
  }
  var linkAttr = linkParam;
  if (!linkAttr) {
    linkAttr = isLinkAnchor ? "href" : "to";
  }
  var wrapperClassNames = classNamesMaker(styles["jds-Pagination__wrapper"], className);
  var dotsClassName = classNamesMaker(styles["jds-Pagination__item"], styles["jds-Pagination__item--dots"]);
  return /*#__PURE__*/React.createElement("nav", {
    role: "navigation",
    className: wrapperClassNames
  }, /*#__PURE__*/React.createElement("ul", {
    className: styles["jds-Pagination"]
  }, /*#__PURE__*/React.createElement("li", {
    className: styles["jds-Pagination__item"],
    key: "prev-".concat(currentPage)
  }, /*#__PURE__*/React.createElement(Button, _extends({
    minor: true,
    icon: IconArrowLeft,
    onClick: onPrevious,
    className: classNamesMaker(styles["jds-Pagination__button"], isFirstPage && styles["jds-Pagination__button--disabled"]),
    disabled: isFirstPage
  }, !isFirstPage && _defineProperty(_defineProperty({
    component: linkComponent
  }, linkAttr, generateLink(currentPage - 1)), "aria-label", ariaLabelPrevButton)))), !withOnlyArrows && paginationRange.map(function (pageNumber, index) {
    var currentIndex = index + currentPage;
    var isCurrentPage = pageNumber === currentPage;
    var isDots = pageNumber === DOTS;
    if (isDots) {
      return /*#__PURE__*/React.createElement("li", {
        className: dotsClassName,
        key: "dots-".concat(currentIndex)
      }, DOTS);
    }
    return /*#__PURE__*/React.createElement("li", {
      className: styles["jds-Pagination__item"],
      key: "item-".concat(pageNumber)
    }, /*#__PURE__*/React.createElement(Button, _extends({
      minor: true,
      component: linkComponent
    }, _defineProperty({}, linkAttr, generateLink(pageNumber)), {
      onClick: function onClick(event) {
        return onPageChange(event, pageNumber);
      }
    }, isCurrentPage && {
      "aria-current": true,
      className: styles["jds-Pagination__activeButton"]
    }, !isCurrentPage && {
      className: styles["jds-Pagination__button"]
    }), pageNumber));
  }), /*#__PURE__*/React.createElement("li", {
    className: styles["jds-Pagination__item"],
    key: "next-".concat(currentPage)
  }, /*#__PURE__*/React.createElement(Button, _extends({
    minor: true,
    icon: IconArrowRight,
    onClick: onNext,
    className: classNamesMaker(styles["jds-Pagination__button"], isLastPage && styles["jds-Pagination__button--disabled"]),
    disabled: isLastPage
  }, !isLastPage && _defineProperty(_defineProperty({
    component: linkComponent
  }, linkAttr, generateLink(currentPage + 1)), "aria-label", ariaLabelNextButton))))));
};
Pagination.propTypes = {
  totalCount: number.isRequired,
  itemPerPage: number,
  className: string,
  onPaginate: func,
  linkComponent: elementType,
  linkParam: string,
  linkPathname: string.isRequired,
  linkSearchParams: string,
  withOnlyArrows: bool,
  withScrollToTop: bool,
  isAutonomous: bool,
  ariaLabelPrevButton: string,
  ariaLabelNextButton: string
};